// External Libraries
import { useState, useEffect } from "react";

// Services
import UserService from "../../services/UserService";
import UtilityService from "../../services/UtilityService";
import GmailService from "../../services/GmailService";

// Common Components
import PageHeading from "../common/page_heading/PageHeading";
import Loader from "../common/loader/Loader";
import Button from "../common/button/Button";

// Styling
import "./user-preferences.css";

const PREFERENCE_DEFS = [
	{
		name: "DEFAULT_VIEW",
		label: "Default View",
		options: [
			{ key: "kanban", label: "Kanban" },
			{ key: "list", label: "List" },
		],
	},
];

const { alert } = UtilityService;

export default function UserPreferences() {
	const [prefs, setPrefs] = useState({});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const [gmailStatus, setGmailStatus] = useState({ connected: false, gmailAddress: null, lastUsedAt: null });
	const [gmailBusy, setGmailBusy] = useState(false);

	useEffect(() => {
		setLoading(true);
		UserService.getPreferences()
			.then((list) => {
				const loaded = list.reduce((acc, { name, value }) => {
					acc[name] = value;
					return acc;
				}, {});
				const initial = {};
				for (let def of PREFERENCE_DEFS) {
					initial[def.name] = loaded[def.name] ?? def.options[0].key;
				}
				setPrefs(initial);
			})
			.catch((err) => console.error("Failed loading prefs:", err))
			.finally(() => setLoading(false));

		refreshGmailStatus();
	}, []);

	const refreshGmailStatus = async () => {
		let status = await GmailService.getStatus();
		setGmailStatus(status);
	};

	const handleChange = (prefName, key) => {
		setPrefs((prev) => ({ ...prev, [prefName]: key }));
	};

	const handleSave = () => {
		setSaving(true);
		UserService.updatePreferences(prefs)
			.then(() => {
				alert({ type: "success", text: "Saved User Preferences!" });
			})
			.catch(() => {
				alert({ type: "error", text: "Error Occured!" });
			})
			.finally(() => setSaving(false));
	};

	const handleConnectGmail = async () => {
		setGmailBusy(true);
		try {
			let authUrlResponse = await GmailService.getAuthUrl();
			if (!authUrlResponse || !authUrlResponse.url) {
				alert({ type: "error", text: "Could not start Google sign-in." });
				return;
			}

			let result = await GmailService.openConsentPopup({ url: authUrlResponse.url });

			if (result.ok) {
				alert({ type: "success", text: "Gmail connected: " + result.gmailAddress });
				await refreshGmailStatus();
			} else {
				alert({ type: "error", text: "Gmail not connected: " + (result.message || "consent cancelled") });
			}
		} finally {
			setGmailBusy(false);
		}
	};

	const handleDisconnectGmail = async () => {
		setGmailBusy(true);
		try {
			let result = await GmailService.disconnect();
			if (result && result.disconnected) {
				alert({ type: "success", text: "Gmail disconnected." });
				await refreshGmailStatus();
			} else {
				alert({ type: "error", text: "Could not disconnect Gmail." });
			}
		} finally {
			setGmailBusy(false);
		}
	};

	return (
		<>
			<div className="user-preferences">
				<PageHeading section="Settings" title={"User Preferences"} />

				{loading && (
					<div className="container-loader">
						<Loader />
					</div>
				)}

				<div className="userpreferences__body">
					{PREFERENCE_DEFS.map((def) => (
						<div key={def.name} className="userpreferences__section userpreferences__form__title">
							<div className="user-preferences__label">{def.label}</div>
							<div className="pill-container">
								{def.options.map((opt) => (
									<div key={opt.key} className={"pill" + (prefs[def.name] === opt.key ? " pill--active" : "")} onClick={() => handleChange(def.name, opt.key)}>
										{opt.label}
									</div>
								))}
							</div>
						</div>
					))}

					<div className="userpreferences__form__footer">
						<Button text={saving ? "Saving…" : "Save Preferences"} onClick={handleSave} disabled={saving} />
					</div>
				</div>

				<div className="userpreferences__body userpreferences__body--gmail">
					<div className="userpreferences__form__title">Connected Accounts</div>

					<div className="gmail-connection">
						<div className="gmail-connection__label">Gmail (send emails as you)</div>

						{gmailStatus.connected ? (
							<div className="gmail-connection__row">
								<div className="gmail-connection__status">
									<span className="gmail-connection__dot gmail-connection__dot--active"></span>
									Connected as <strong>{gmailStatus.gmailAddress}</strong>
								</div>
								<Button text={gmailBusy ? "Working…" : "Disconnect"} onClick={handleDisconnectGmail} disabled={gmailBusy} />
							</div>
						) : (
							<div className="gmail-connection__row">
								<div className="gmail-connection__status">
									<span className="gmail-connection__dot"></span>
									Not connected. Bookkeeping client requests will be sent from the system address.
								</div>
								<Button text={gmailBusy ? "Working…" : "Connect Gmail"} onClick={handleConnectGmail} disabled={gmailBusy} />
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
}
